import { WrappedData } from './types.js';

export interface PeopleQuizQuestion {
  id: number;
  question: string;
  emoji: string;
  funTitle: string;
  options: string[]; // List of people names
  correctIndex: number;
  correctPerson: string;
  correctValue: number | string;
  explanation: string;
  // Stats reveal with concrete example
  statsReveal: {
    title: string;
    emoji: string;
    subtitle: string;
    mainStat: {
      label: string;
      value: string | number;
    };
    example?: {
      type: 'file' | 'time' | 'streak' | 'text';
      label: string;
      content: string;
      icon?: string;
    };
    topContributors?: Array<{ name: string; value: number | string; isWinner: boolean }>;
    funFact?: string;
  };
}

export class PeopleQuizGenerator {
  private questions: PeopleQuizQuestion[] = [];
  private authors: string[] = [];

  constructor(private data: WrappedData) {
    this.authors = Object.keys(this.data.commitsByAuthor);
    this.generateAllQuestions();
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private getRandomOptions(correctPerson: string, count: number = 4): string[] {
    const others = this.authors.filter(a => a !== correctPerson);
    const shuffledOthers = this.shuffleArray(others).slice(0, count - 1);
    const options = [correctPerson, ...shuffledOthers];
    return this.shuffleArray(options);
  }

  private generateAllQuestions(): void {
    const allQuestions: PeopleQuizQuestion[] = [];

    // Different types of questions with concrete examples
    const nightOwl = this.createNightOwlQuestion();
    if (nightOwl) allQuestions.push(nightOwl);

    const earlyBird = this.createEarlyBirdQuestion();
    if (earlyBird) allQuestions.push(earlyBird);

    const weekend = this.createWeekendWarriorQuestion();
    if (weekend) allQuestions.push(weekend);

    const friday = this.createFridayDeployerQuestion();
    if (friday) allQuestions.push(friday);

    const streak = this.createStreakQuestion();
    if (streak) allQuestions.push(streak);

    const activeDays = this.createActiveDaysQuestion();
    if (activeDays) allQuestions.push(activeDays);

    const commits = this.createMostCommitsQuestion();
    if (commits) allQuestions.push(commits);

    const linesAdded = this.createLinesAddedQuestion();
    if (linesAdded) allQuestions.push(linesAdded);

    const linesDeleted = this.createLinesDeletedQuestion();
    if (linesDeleted) allQuestions.push(linesDeleted);

    const prs = this.createPRsQuestion();
    if (prs) allQuestions.push(prs);

    const topFile = this.createTopFileQuestion();
    if (topFile) allQuestions.push(topFile);

    // Shuffle and assign IDs
    this.questions = this.shuffleArray(allQuestions);
    this.questions.forEach((q, i) => q.id = i + 1);
  }

  private createNightOwlQuestion(): PeopleQuizQuestion | null {
    const nightOwlData = Object.entries(this.data.nightOwlCommits)
      .filter(([_, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    
    if (nightOwlData.length === 0) return null;

    const [winner, winnerValue] = nightOwlData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = nightOwlData.slice(0, 5).map(([name, value]) => ({
      name,
      value,
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui est le hibou de l'equipe? (commits apres 22h)",
      emoji: '🦉',
      funTitle: 'Le Hibou Nocturne',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a fait ${winnerValue} commits apres 22h!`,
      statsReveal: {
        title: winner,
        emoji: '🦉',
        subtitle: 'Le Hibou Nocturne',
        mainStat: {
          label: 'Commits apres 22h',
          value: `${winnerValue} commits`
        },
        example: {
          type: 'time',
          label: 'Heure favorite',
          content: this.data.mostActiveHourByAuthor[winner]?.hour || '22:00',
          icon: '🕙'
        },
        topContributors,
        funFact: winnerValue > 10 ? 'Les meilleures idees viennent la nuit!' : 'Quelques nuits blanches productives!'
      }
    };
  }

  private createEarlyBirdQuestion(): PeopleQuizQuestion | null {
    const earlyBirdData = Object.entries(this.data.earlyBirdCommits || {})
      .filter(([_, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    
    if (earlyBirdData.length === 0) return null;

    const [winner, winnerValue] = earlyBirdData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = earlyBirdData.slice(0, 5).map(([name, value]) => ({
      name,
      value,
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui se leve aux aurores pour coder? (commits avant 9h)",
      emoji: '🌅',
      funTitle: 'Le Leve-Tot',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a fait ${winnerValue} commits avant 9h!`,
      statsReveal: {
        title: winner,
        emoji: '🌅',
        subtitle: 'Le Leve-Tot',
        mainStat: {
          label: 'Commits avant 9h',
          value: `${winnerValue} commits`
        },
        example: {
          type: 'time',
          label: 'Heure favorite',
          content: this.data.mostActiveHourByAuthor[winner]?.hour || '08:00',
          icon: '⏰'
        },
        topContributors,
        funFact: 'Le monde appartient a ceux qui se levent tot!'
      }
    };
  }

  private createWeekendWarriorQuestion(): PeopleQuizQuestion | null {
    const weekendData = Object.entries(this.data.weekendWarriorCommits || {})
      .filter(([_, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    
    if (weekendData.length === 0) return null;

    const [winner, winnerValue] = weekendData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = weekendData.slice(0, 5).map(([name, value]) => ({
      name,
      value,
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui ne connait pas le repos du weekend?",
      emoji: '🏋️',
      funTitle: 'Le Guerrier du Weekend',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a fait ${winnerValue} commits le weekend!`,
      statsReveal: {
        title: winner,
        emoji: '🏋️',
        subtitle: 'Le Guerrier du Weekend',
        mainStat: {
          label: 'Commits le weekend',
          value: `${winnerValue} commits`
        },
        topContributors,
        funFact: 'Le code n\'attend pas le lundi!'
      }
    };
  }

  private createFridayDeployerQuestion(): PeopleQuizQuestion | null {
    const fridayData = Object.entries(this.data.fridayWarriorCommits || {})
      .filter(([_, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    
    if (fridayData.length === 0) return null;

    const [winner, winnerValue] = fridayData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = fridayData.slice(0, 5).map(([name, value]) => ({
      name,
      value,
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui aime deployer le vendredi? (YOLO!)",
      emoji: '🎉',
      funTitle: 'Le Friday Deployer',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a fait ${winnerValue} commits le vendredi!`,
      statsReveal: {
        title: winner,
        emoji: '🎉',
        subtitle: 'Le Friday Deployer',
        mainStat: {
          label: 'Commits le vendredi',
          value: `${winnerValue} commits`
        },
        topContributors,
        funFact: 'Vendredi deploy, weekend debug!'
      }
    };
  }

  private createStreakQuestion(): PeopleQuizQuestion | null {
    const streakData = Object.entries(this.data.longestStreakByAuthor)
      .filter(([_, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    
    if (streakData.length === 0) return null;

    const [winner, winnerValue] = streakData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = streakData.slice(0, 5).map(([name, value]) => ({
      name,
      value,
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui a la plus longue serie de commits consecutifs?",
      emoji: '🔥',
      funTitle: 'Le Marathonien',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a tenu ${winnerValue} jours consecutifs!`,
      statsReveal: {
        title: winner,
        emoji: '🔥',
        subtitle: 'Le Marathonien',
        mainStat: {
          label: 'Serie la plus longue',
          value: `${winnerValue} jours consecutifs`
        },
        example: {
          type: 'streak',
          label: 'Record',
          content: `${winnerValue} jours d'affilee sans interruption`,
          icon: '🏃'
        },
        topContributors,
        funFact: winnerValue > 30 ? 'Incroyable determination!' : 'Belle perseverance!'
      }
    };
  }

  private createActiveDaysQuestion(): PeopleQuizQuestion | null {
    const activeDaysData = Object.entries(this.data.uniqueActiveDaysByAuthor || {})
      .filter(([_, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    
    if (activeDaysData.length === 0) return null;

    const [winner, winnerValue] = activeDaysData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = activeDaysData.slice(0, 5).map(([name, value]) => ({
      name,
      value,
      isWinner: name === winner
    }));

    const percentage = Math.round((winnerValue / 365) * 100);

    return {
      id: 0,
      question: "Qui a ete actif le plus grand nombre de jours?",
      emoji: '📅',
      funTitle: 'Le Plus Regulier',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a code ${winnerValue} jours differents!`,
      statsReveal: {
        title: winner,
        emoji: '📅',
        subtitle: 'Le Plus Regulier',
        mainStat: {
          label: 'Jours actifs',
          value: `${winnerValue} jours (${percentage}% de l'annee)`
        },
        topContributors,
        funFact: percentage > 50 ? 'Plus d\'un jour sur deux!' : 'Belle regularite!'
      }
    };
  }

  private createMostCommitsQuestion(): PeopleQuizQuestion | null {
    const commitsData = Object.entries(this.data.commitsByAuthor)
      .sort((a, b) => b[1] - a[1]);
    
    if (commitsData.length === 0) return null;

    const [winner, winnerValue] = commitsData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = commitsData.slice(0, 5).map(([name, value]) => ({
      name,
      value: value.toLocaleString(),
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui a fait le plus de commits cette annee?",
      emoji: '🚀',
      funTitle: 'Le Champion des Commits',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a fait ${winnerValue.toLocaleString()} commits!`,
      statsReveal: {
        title: winner,
        emoji: '🚀',
        subtitle: 'Le Champion des Commits',
        mainStat: {
          label: 'Total de commits',
          value: `${winnerValue.toLocaleString()} commits`
        },
        topContributors,
        funFact: 'Productivite au maximum!'
      }
    };
  }

  private createLinesAddedQuestion(): PeopleQuizQuestion | null {
    const linesData = Object.entries(this.data.linesChangedByAuthor)
      .map(([name, data]) => [name, data.added] as [string, number])
      .sort((a, b) => b[1] - a[1]);
    
    if (linesData.length === 0) return null;

    const [winner, winnerValue] = linesData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = linesData.slice(0, 5).map(([name, value]) => ({
      name,
      value: value.toLocaleString(),
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui a ajoute le plus de lignes de code?",
      emoji: '📝',
      funTitle: 'Le Batisseur',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a ajoute ${winnerValue.toLocaleString()} lignes!`,
      statsReveal: {
        title: winner,
        emoji: '📝',
        subtitle: 'Le Batisseur',
        mainStat: {
          label: 'Lignes ajoutees',
          value: `${winnerValue.toLocaleString()} lignes`
        },
        topContributors,
        funFact: 'Tant de nouvelles fonctionnalites!'
      }
    };
  }

  private createLinesDeletedQuestion(): PeopleQuizQuestion | null {
    const linesData = Object.entries(this.data.linesChangedByAuthor)
      .map(([name, data]) => [name, data.removed] as [string, number])
      .sort((a, b) => b[1] - a[1]);
    
    if (linesData.length === 0) return null;

    const [winner, winnerValue] = linesData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = linesData.slice(0, 5).map(([name, value]) => ({
      name,
      value: value.toLocaleString(),
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui est le champion du refactoring?",
      emoji: '🧹',
      funTitle: 'Le Nettoyeur',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a supprime ${winnerValue.toLocaleString()} lignes!`,
      statsReveal: {
        title: winner,
        emoji: '🧹',
        subtitle: 'Le Nettoyeur',
        mainStat: {
          label: 'Lignes supprimees',
          value: `${winnerValue.toLocaleString()} lignes`
        },
        topContributors,
        funFact: 'Code propre = code efficace!'
      }
    };
  }

  private createPRsQuestion(): PeopleQuizQuestion | null {
    if (!this.data.pullRequests) return null;

    const prsData = Object.entries(this.data.pullRequests.prsByAuthor)
      .map(([name, data]) => [name, data.merged] as [string, number])
      .filter(([_, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    
    if (prsData.length === 0) return null;

    const [winner, winnerValue] = prsData[0];
    const options = this.getRandomOptions(winner);
    const topContributors = prsData.slice(0, 5).map(([name, value]) => ({
      name,
      value,
      isWinner: name === winner
    }));

    return {
      id: 0,
      question: "Qui a fait merger le plus de Pull Requests?",
      emoji: '✅',
      funTitle: 'Le PR Master',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: winnerValue,
      explanation: `${winner} a merge ${winnerValue} PRs!`,
      statsReveal: {
        title: winner,
        emoji: '✅',
        subtitle: 'Le PR Master',
        mainStat: {
          label: 'Pull Requests mergees',
          value: `${winnerValue} PRs`
        },
        topContributors,
        funFact: 'La collaboration est cle!'
      }
    };
  }

  private createTopFileQuestion(): PeopleQuizQuestion | null {
    // Find who has the file with most commits
    let maxCommits = 0;
    let winner = '';
    let topFile = '';

    Object.entries(this.data.topFilesByAuthor || {}).forEach(([author, files]) => {
      if (files.length > 0 && files[0].commits > maxCommits) {
        maxCommits = files[0].commits;
        winner = author;
        topFile = files[0].file;
      }
    });

    if (!winner || maxCommits === 0) return null;

    const options = this.getRandomOptions(winner);
    
    // Extract filename from path
    const fileName = topFile.split('/').pop() || topFile;
    const shortPath = topFile.length > 60 ? '...' + topFile.slice(-57) : topFile;

    return {
      id: 0,
      question: "Qui a le fichier le plus modifie?",
      emoji: '📂',
      funTitle: 'Le Specialiste',
      options,
      correctIndex: options.indexOf(winner),
      correctPerson: winner,
      correctValue: maxCommits,
      explanation: `${winner} a modifie un fichier ${maxCommits} fois!`,
      statsReveal: {
        title: winner,
        emoji: '📂',
        subtitle: 'Le Specialiste',
        mainStat: {
          label: 'Fichier le plus modifie',
          value: `${maxCommits} commits`
        },
        example: {
          type: 'file',
          label: 'Fichier',
          content: shortPath,
          icon: '📄'
        },
        funFact: `Expert de ${fileName}!`
      }
    };
  }

  getQuestions(): PeopleQuizQuestion[] {
    return this.questions;
  }

  getQuestionCount(): number {
    return this.questions.length;
  }
}
